import { Loader } from "@mantine/core";
import {
    CollectionReference,
    DocumentData,
    getCountFromServer,
    getDocs,
    limit,
    orderBy,
    query,
    QueryDocumentSnapshot,
    startAfter,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";

type HistoryRow = DocumentData & {
    id: string;
    msg?: string;
    by?: string;
    details?: string;
    date?: Date | { toDate: () => Date } | { seconds: number } | null;
};

type HistoryProps = {
    historyCollection: CollectionReference<DocumentData>;
    defaultRowsPerPage?: number;
};

const formatHistoryDate = (row: HistoryRow): string => {
    const dateValue = row.date;
    if (!dateValue) {
        return "";
    }

    if (dateValue instanceof Date) {
        return dateValue.toLocaleString("en-GB", { hour12: true });
    }

    if (typeof dateValue?.toDate === "function") {
        return dateValue.toDate().toLocaleString("en-GB", { hour12: true });
    }

    if (typeof dateValue?.seconds === "number") {
        return new Date(dateValue.seconds * 1000).toLocaleString("en-GB", { hour12: true });
    }

    return "";
};

const columns: TableColumn<HistoryRow>[] = [
    {
        name: "Action",
        selector: (row) => row.msg ?? "",
    },
    {
        name: "by",
        selector: (row) => row.by ?? "",
    },
    {
        name: "Date",
        selector: (row) => formatHistoryDate(row),
        sortable: true,
    },
    {
        name: "Details",
        //one change per line (service history); inline style, since the table forces white-space on its own cell div
        cell: (row) => <div style={{ whiteSpace: "pre-line" }}>{row.details ?? ""}</div>,
        wrap: true,
    },
];

export const History = ({ historyCollection, defaultRowsPerPage = 100 }: HistoryProps) => {
    const [historyLoading, setHistoryLoading] = useState<boolean>(true);
    const [historyData, setHistoryData] = useState<HistoryRow[]>([]);
    const [page, setPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [resetPaginationToggle, setResetPaginationToggle] = useState<boolean>(false);
    const pageCursorRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData>>>(new Map());
    const collectionPathRef = useRef<string>(historyCollection.path);
    const requestIdRef = useRef<number>(0);

    useEffect(() => {
        let cancelled = false;
        setTotalRows(0);

        const fetchTotalRows = async () => {
            try {
                const countSnapshot = await getCountFromServer(query(historyCollection));
                if (!cancelled) {
                    setTotalRows(countSnapshot.data().count);
                }
            } catch (error) {
                console.error("Unable to load history count", error);
                if (!cancelled) {
                    setTotalRows(0);
                }
            }
        };

        void fetchTotalRows();

        return () => {
            cancelled = true;
        };
    }, [historyCollection]);

    useEffect(() => {
        let cancelled = false;
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        if (collectionPathRef.current !== historyCollection.path) {
            collectionPathRef.current = historyCollection.path;
            pageCursorRef.current.clear();
            setPage(1);
            setHistoryData([]);
            setHistoryLoading(true);
            setResetPaginationToggle((currentValue) => !currentValue);
            return () => {
                cancelled = true;
            };
        }

        const getCursorBeforePage = async (targetPage: number) => {
            if (targetPage <= 1) {
                return undefined;
            }

            const cachedCursor = pageCursorRef.current.get(targetPage - 1);
            if (cachedCursor) {
                return cachedCursor;
            }

            let startPage = 1;
            let previousCursor: QueryDocumentSnapshot<DocumentData> | undefined = undefined;
            const knownPages = Array.from(pageCursorRef.current.keys()).sort((a, b) => a - b);

            for (const knownPage of knownPages) {
                if (knownPage < targetPage - 1) {
                    startPage = knownPage + 1;
                    previousCursor = pageCursorRef.current.get(knownPage);
                } else {
                    break;
                }
            }

            for (let pageIndex = startPage; pageIndex < targetPage; pageIndex += 1) {
                const cursorQuery = previousCursor
                    ? query(historyCollection, orderBy("date", "desc"), startAfter(previousCursor), limit(rowsPerPage))
                    : query(historyCollection, orderBy("date", "desc"), limit(rowsPerPage));
                const cursorSnapshot = await getDocs(cursorQuery);

                if (cancelled || requestId !== requestIdRef.current) {
                    return undefined;
                }

                if (cursorSnapshot.empty) {
                    return undefined;
                }

                previousCursor = cursorSnapshot.docs[cursorSnapshot.docs.length - 1];
                pageCursorRef.current.set(pageIndex, previousCursor);
            }

            return pageCursorRef.current.get(targetPage - 1);
        };

        const fetchPage = async () => {
            setHistoryLoading(true);

            try {
                const previousCursor = await getCursorBeforePage(page);
                if (cancelled || requestId !== requestIdRef.current) {
                    return;
                }

                if (page > 1 && !previousCursor) {
                    setHistoryData([]);
                    return;
                }

                const historyQuery = previousCursor
                    ? query(historyCollection, orderBy("date", "desc"), startAfter(previousCursor), limit(rowsPerPage))
                    : query(historyCollection, orderBy("date", "desc"), limit(rowsPerPage));
                const historySnapshot = await getDocs(historyQuery);

                if (cancelled || requestId !== requestIdRef.current) {
                    return;
                }

                if (!historySnapshot.empty) {
                    pageCursorRef.current.set(page, historySnapshot.docs[historySnapshot.docs.length - 1]);
                }

                const rows: HistoryRow[] = historySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setHistoryData(rows);
            } catch (error) {
                console.error("Unable to load history page", error);
                if (!cancelled && requestId === requestIdRef.current) {
                    setHistoryData([]);
                }
            } finally {
                if (!cancelled && requestId === requestIdRef.current) {
                    setHistoryLoading(false);
                }
            }
        };

        void fetchPage();

        return () => {
            cancelled = true;
        };
    }, [historyCollection, page, rowsPerPage]);

    return (
        <DataTable
            dense
            columns={columns}
            data={historyData}
            progressPending={historyLoading}
            progressComponent={<Loader />}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={rowsPerPage}
            paginationRowsPerPageOptions={[25, 50, 100, 250]}
            paginationResetDefaultPage={resetPaginationToggle}
            onChangePage={(value: number) => setPage(value)}
            onChangeRowsPerPage={(value: number) => {
                pageCursorRef.current.clear();
                setRowsPerPage(value);
                setPage(1);
                setResetPaginationToggle((currentValue) => !currentValue);
            }}
        />
    );
};
